package er.prateek.crossly.generation

import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.data.model.WordBank
import er.prateek.crossly.game.crossword.stableSeed

/**
 * Builds a whole tier's worth of levels from a [WordBank].
 *
 * A tier is produced as one sequence rather than puzzle-by-puzzle, because
 * freshness is a property of the sequence: each puzzle is generated knowing
 * which answers the previous ones used, so a player working through a tier
 * doesn't keep meeting the same words.
 *
 * Pure and synchronous by design. This is the single implementation shared by
 * the build-time pack generator and any runtime use, so a level can never
 * differ depending on which one produced it. Determinism is the point: stars
 * are stored against puzzle ids, so `easy-gen-007` must be the same puzzle on
 * every device and build -- hence seeds from [stableSeed] rather than anything
 * ambient like a clock or `hashCode()`.
 */
class TierGenerator(
    bank: WordBank,
    private val puzzlesPerTier: Int = 30,
) {
    private val generator = PuzzleGenerator(bank)

    /**
     * How many previous puzzles' answers stay off-limits. Sized against the
     * shallowest pool: EASY 3-letter words are the scarcest bucket, so a window
     * much wider than this leaves the search nothing to work with.
     */
    private val freshnessWindow = 10

    fun generateTier(tier: DifficultyTier, startIndex: Int = 1): List<Puzzle> {
        val templates = GridTemplates.forTier(tier)
        val recent = ArrayDeque<Set<String>>()
        val puzzles = mutableListOf<Puzzle>()

        var index = startIndex
        while (puzzles.size < puzzlesPerTier) {
            val id = "${tier.name.lowercase()}-gen-${index.toString().padStart(3, '0')}"
            val template = templates[(index - 1) % templates.size]
            val avoid = recent.flatten().toSet()

            // Two-stage: insist on unseen answers first, and only relax once the
            // strict pass has run out of seeds. Relaxing immediately would let
            // the search fall back on the same well-crossing words every time,
            // which is what makes a tier feel repetitive even though no single
            // puzzle repeats a word.
            var puzzle: Puzzle? = null
            for (attempt in 0 until MAX_SEED_ATTEMPTS) {
                puzzle = generator.generate(
                    template = template,
                    tier = tier,
                    seed = id.stableSeed() + attempt,
                    id = id,
                    title = "Level ${puzzles.size + 1}",
                    avoidWords = avoid,
                    avoidStrictly = attempt < STRICT_ATTEMPTS,
                )
                if (puzzle != null) break
            }
            index++

            if (puzzle == null) {
                // Ran out of seeds for this id; skip it rather than loop forever.
                if (index > startIndex + puzzlesPerTier * 4) break
                continue
            }

            puzzles.add(puzzle)
            recent.addLast(puzzle.answerSet())
            if (recent.size > freshnessWindow) recent.removeFirst()
        }

        return puzzles
    }

    private companion object {
        const val MAX_SEED_ATTEMPTS = 8

        /** Seeds spent demanding entirely unseen answers before relaxing. */
        const val STRICT_ATTEMPTS = 6
    }
}

internal fun Puzzle.answerSet(): Set<String> =
    (cluesAcross + cluesDown).mapTo(mutableSetOf()) { it.answer }
